/**
 * # dxfBridge.conformantSkeleton.dimBlocks (T9b — typed anonymous dimension blocks)
 *
 * AutoCAD requires every DIMENSION to reference an anonymous `*D<n>` block
 * (group 2) that owns its graphical substitution; the block needs a BLOCK +
 * ENDBLK (each with its own group-5 handle) and a matching BLOCK_RECORD. The
 * dxf-io writer used to stub a handle-less BLOCK/ENDBLK for an unnamed
 * dimension and NO BLOCK_RECORD at all, so ezdxf silently minted all three on
 * load (AutoCAD recovers the file). On the FROM-SCRATCH conformant path the
 * anonymous block is therefore materialised in the TYPED model FIRST — a
 * `BlockDefinition` (flags 1 = anonymous, empty, base point 0) registered under
 * the allocated `*D<n>` name — so the standard record machinery mints its
 * BLOCK / ENDBLK / BLOCK_RECORD handles, wires the owners and emits it exactly
 * like a user block. Modern readers (AutoCAD, ezdxf) recompute the dimension
 * geometry from the DIMENSION fields when the block is empty (ezdxf
 * `Dimension.render` / AutoCAD DIMREGEN), so an empty anonymous block is the
 * documented shape, not a placeholder.
 *
 * Source of truth: Autodesk DXF reference (DIMENSION group 2, BLOCK flags) +
 * ezdxf 1.4.3 `Dimension` / `BlockLayout`.
 */
import { nextAnonymousBlockName } from "./blockNaming";
import { visitEmittedEntityLists } from "./dxfBridge.conformantSkeleton.emittedEntities";
import {
  blockDefinitionSchema,
  blockRecordSchema,
  type BlockDefinition,
  type BlockRecord,
  type DrawingDocument,
} from "./model";

/** Structural shape of a dimension entity as far as this pass cares. */
interface DimensionLike {
  type?: string;
  anonymousBlockName?: string;
}

/** Result of {@link assignAnonymousDimensionBlocks}. */
export interface AnonymousDimensionBlockAssignment {
  /**
   * Source dimension entity OBJECT → the `*D<n>` names allocated to it, ONE PER
   * OCCURRENCE in visit order. Keyed by identity (not id) because the skeleton
   * pass clones entities per occurrence; the caller consumes the names in the
   * same visit order via {@link takeAnonymousDimensionBlockName} and stamps
   * `anonymousBlockName` on each clone.
   *
   * T9b review round 1 (P2) — a single `Map<object, string>` was unsound when
   * the SAME dimension object sits at two array positions: two blocks were
   * allocated but only the LAST name survived, so both clones referenced it
   * and the first `*D<n>` block was emitted orphaned. A queue per object
   * mirrors the skeleton's per-occurrence entity handles.
   */
  nameByEntity: Map<object, string[]>;
  /** The freshly synthesized anonymous blocks (name → definition). */
  blocks: Record<string, BlockDefinition>;
  /**
   * T9b review round 3 (P1) — the same-named typed BLOCK_RECORD of every
   * synthesized block (name → record). Created HERE, with the block, so the
   * skeleton's record machinery (`remintRecordOwnHandles` collision set /
   * remap, `wireSymbolTableOwners` `330` = BLOCK_RECORD head, the FIX-202
   * census and the `$HANDSEED` ceiling) treats it exactly like a user block
   * record. Before this the record was synthesized by the skeleton's
   * BLOCK ⇄ BLOCK_RECORD completeness fallback AFTER the remint — inside the
   * ceiling and owned, but outside the record remint contract.
   */
  blockRecords: Record<string, BlockRecord>;
}

/**
 * Consume the next `*D<n>` name allocated to `entity` (FIFO — the caller visits
 * clones in the same order {@link assignAnonymousDimensionBlocks} visited the
 * sources). `undefined` when the entity is not an unnamed dimension or every
 * allocated name has already been consumed.
 */
export function takeAnonymousDimensionBlockName(
  assignment: Pick<AnonymousDimensionBlockAssignment, "nameByEntity">,
  entity: object,
): string | undefined {
  const queue = assignment.nameByEntity.get(entity);
  if (!queue || queue.length === 0) return undefined;
  return queue.shift();
}

/**
 * Walk every EMITTED dimension ({@link visitEmittedEntityLists}: top-level
 * spaces except the transient block-test / block-edit sandboxes, plus every
 * block's children) and
 * make sure each references an anonymous `*D<n>` block that EXISTS in
 * `existingBlocks`. An unnamed dimension gets the next free `*D<n>`; a named
 * dimension whose block is missing gets that block synthesized under its own
 * name (never renamed). Every synthesized block comes with its same-named
 * typed BLOCK_RECORD. Pure: returns the new blocks + records + the per-entity
 * mapping; neither input is mutated.
 */
export function assignAnonymousDimensionBlocks(
  document: DrawingDocument,
  existingBlocks: Readonly<Record<string, unknown>>,
): AnonymousDimensionBlockAssignment {
  const nameByEntity = new Map<object, string[]>();
  const blocks: Record<string, BlockDefinition> = {};
  const blockRecords: Record<string, BlockRecord> = {};
  // Case-insensitive view of every block name already taken (existing + newly
  // synthesized) — DXF block names are case-insensitive.
  const taken: Record<string, true> = {};
  for (const name of Object.keys(existingBlocks)) taken[name.toUpperCase()] = true;

  const ensureBlock = (name: string): void => {
    if (taken[name.toUpperCase()]) return;
    taken[name.toUpperCase()] = true;
    // NOTE: the block is EMPTY (exactly like the old writer stub) — only the
    // record/handle/owner structure is materialised here. Generating the
    // dimension's block geometry (the AutoCAD DIMREGEN content) is a separate
    // task; readers recompute the display from the DIMENSION fields meanwhile.
    blocks[name] = blockDefinitionSchema.parse({
      name,
      handle: "",
      layer: "0",
      flags: 1, // anonymous block (Autodesk BLOCK group 70 bit 1)
      basePoint: { x: 0, y: 0, z: 0 },
      entities: [],
    });
    // The typed default record (units 0, explodable, non-uniform, not
    // annotative) — the shape AutoCAD writes for an anonymous `*D` record.
    blockRecords[name] = blockRecordSchema.parse({ name });
  };

  const visit = (entities: readonly unknown[]): void => {
    for (const entity of entities) {
      const dim = entity as DimensionLike;
      if (dim.type !== "Dimension") continue;
      const current = dim.anonymousBlockName?.trim() ?? "";
      if (current.length > 0) {
        ensureBlock(current);
        continue;
      }
      const name = nextAnonymousBlockName("*D", taken);
      ensureBlock(name);
      const queue = nameByEntity.get(entity as object);
      if (queue) queue.push(name);
      else nameByEntity.set(entity as object, [name]);
    }
  };

  visitEmittedEntityLists(document, visit);
  return { nameByEntity, blocks, blockRecords };
}
